import React, { useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Divider, IconButton, Typography, Box, Button } from '@mui/material';
import { CloudUpload, Close } from '@mui/icons-material';
// import { ReactComponent as DeleteIcon } from '../../../assets/SVG/trash.svg';
import ImageItem from './imageItem';
import { UploadedImageType } from './hysteroscopy.helper';

interface ImageUploadModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (images: UploadedImageType[]) => void;
}

const ImageUploadModal: React.FC<ImageUploadModalProps> = ({ open, onClose, onSave }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [tempUploadedImages, setTempUploadedImages] = useState<UploadedImageType[]>([]);
 
  const handleFiles = (files: File[]) => {
    const fileReaders = files.map(file => {
      return new Promise<UploadedImageType>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          let result = reader.result as string;
          // Fix PDF Data URL if needed
          if (
            file.type === 'application/pdf' &&
            result.startsWith('data:;base64,')
          ) {
            result = result.replace('data:;base64,', 'data:application/pdf;base64,');
          }
          resolve({
            name: file.name,
            image: result,
            size: (file.size / (1024 * 1024)).toFixed(2),
          });
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    });

    Promise.all(fileReaders).then(images => {
      setTempUploadedImages(prev => [...prev, ...images]);
    });
  };


  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  // Define handleDragOver to prevent default dragover behavior
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    handleFiles(files);
  };

  const handleRemoveImage = (index: number) => {
    setTempUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveImages = () => {
    onSave(tempUploadedImages);
    setTempUploadedImages([]); // Clear after save
    onClose();
  };

  const handleCloseModal = () => {
    setTempUploadedImages([]); // Clear on close
    onClose();
  };
  
  const commonButtonStyles = {
    width: '48%',
    borderRadius: '16px',
    fontWeight: 'bold',
    fontSize: '1.1rem',
    textTransform: 'capitalize',
    transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
  };


  return (
    <Dialog open={open} onClose={handleCloseModal} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 'bold', fontSize: '20px', p: 2 }}>
        Upload Image
        <IconButton onClick={handleCloseModal} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <Divider sx={{ borderBottom: '2px solid #E0E0E0' }} />

      <DialogContent sx={{ p: 2 }}>
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}  // Attach handleDragOver here
          onClick={() => fileInputRef.current?.click()}
          sx={{
            width: '100%',
            height: 200,
            border: '1px dashed #BDBDBD',
            borderRadius: 2,
            backgroundColor: '#FAFAFA',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            textAlign: 'center',
            p: 2,
            mb: 2,
          }}
        >
          <CloudUpload fontSize="large" color="action" />
          <Typography sx={{ mt: 1 }}>
            Drag and Drop or{' '}
            <Box component="span" sx={{ color: '#5A8AEA', fontWeight: 500, cursor: 'pointer' }}>
              Click to Upload
            </Box>
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Supported formats: png, jpeg. Max Size: 20MB
          </Typography>
          <input ref={fileInputRef} type="file" multiple hidden onChange={handleImageUpload} />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight:'300px', overflow:'auto' }}>
          {tempUploadedImages.map((image, index) => (
            <ImageItem key={index} image={image} index={index} onRemove={handleRemoveImage} />
          ))}
        </Box>
      </DialogContent>

      <Divider sx={{ borderBottom: '2px solid #E0E0E0' }} />

      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>


        <Button
          variant="outlined"
          onClick={handleCloseModal}
          sx={{
            ...commonButtonStyles,
            color: '#444',
            borderColor: '#444',
            '&:hover': {
              backgroundColor: '#f5f5f5',
              borderColor: '#222',
              transform: 'scale(1.03)',
            },
          }}
        >
  Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSaveImages}
          disabled={tempUploadedImages.length === 0}
          sx={{
            ...commonButtonStyles,
            backgroundColor: '#555',
            color: '#fff',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#333',
              transform: 'scale(1.03)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
            },
            '&.Mui-disabled': {
              backgroundColor: '#bbb',
              color: '#fff',
            },
          }}
        >
  Save
        </Button>

      </DialogActions>
    </Dialog>
  );
};

export default ImageUploadModal;
